export const DEFAULT_MAX_EDGE = 3000
const SKIP_BYTES = 3 * 1024 * 1024
const JPEG_QUALITY = 0.85

export interface PreparedFile {
  blob: Blob
  mime: string
  /** Pixel size; 0 when the browser could not decode the original (kept as is). */
  width: number
  height: number
  bytes: number
  name: string
}

export interface DecodedImage {
  width: number
  height: number
}

/** Browser primitives, injectable so the sizing logic is unit-testable without a canvas. */
export interface ImageDeps {
  decode(file: Blob): Promise<DecodedImage>
  hasTransparency(image: DecodedImage): boolean
  encode(image: DecodedImage, width: number, height: number, mime: string, quality: number): Promise<Blob | null>
}

export class UnsupportedImageError extends Error {
  constructor(public readonly heic: boolean) {
    super(heic ? 'heic_unsupported' : 'image_decode_failed')
  }
}

export interface DownscaleOptions {
  maxEdge?: number
  /** Upload the file unchanged ("keep original quality"). HEIC is still converted. */
  keepOriginal?: boolean
}

/** Long edge bounded to maxEdge, aspect ratio preserved, never upscaled. */
export function computeTargetSize(width: number, height: number, maxEdge: number): { width: number; height: number } {
  const longEdge = Math.max(width, height)
  if (longEdge <= maxEdge) return { width, height }
  const scale = maxEdge / longEdge
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

function isHeic(file: File): boolean {
  const type = file.type.toLowerCase()
  const name = file.name.toLowerCase()
  return type === 'image/heic' || type === 'image/heif' || name.endsWith('.heic') || name.endsWith('.heif')
}

function replaceExtension(name: string, extension: string): string {
  const dot = name.lastIndexOf('.')
  return `${dot === -1 ? name : name.slice(0, dot)}.${extension}`
}

function original(file: File, size: DecodedImage | null): PreparedFile {
  return {
    blob: file,
    mime: file.type,
    width: size?.width ?? 0,
    height: size?.height ?? 0,
    bytes: file.size,
    name: file.name,
  }
}

export const browserImageDeps: ImageDeps = {
  async decode(file: Blob): Promise<DecodedImage> {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
    return bitmap
  },
  hasTransparency(image: DecodedImage): boolean {
    const bitmap = image as ImageBitmap
    const sample = 64
    const canvas = document.createElement('canvas')
    canvas.width = sample
    canvas.height = sample
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (ctx === null) return false
    ctx.drawImage(bitmap, 0, 0, sample, sample)
    const pixels = ctx.getImageData(0, 0, sample, sample).data
    for (let i = 3; i < pixels.length; i += 4) {
      if ((pixels[i] ?? 255) < 255) return true
    }
    return false
  },
  async encode(image, width, height, mime, quality): Promise<Blob | null> {
    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (ctx === null) return null
    ctx.drawImage(image as ImageBitmap, 0, 0, width, height)
    return new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mime, quality))
  },
}

/**
 * Prepares a photo for upload: bounds the long edge to about 3000 px, re-encodes to JPEG
 * (PNG when the image has transparency), which also bakes in the EXIF orientation and drops
 * EXIF/GPS. Skips re-encoding small files and keeps the original when re-encoding would be
 * larger. HEIC/HEIF is always converted; without browser support it is rejected.
 */
export async function downscaleImage(
  file: File,
  options: DownscaleOptions = {},
  deps: ImageDeps = browserImageDeps,
): Promise<PreparedFile> {
  const maxEdge = options.maxEdge ?? DEFAULT_MAX_EDGE
  const heic = isHeic(file)

  let image: DecodedImage
  try {
    image = await deps.decode(file)
  } catch {
    if (heic) throw new UnsupportedImageError(true)
    if (options.keepOriginal) return original(file, null)
    throw new UnsupportedImageError(false)
  }

  if (!heic) {
    if (options.keepOriginal) return original(file, image)
    const small = Math.max(image.width, image.height) <= maxEdge && file.size <= SKIP_BYTES
    if (small) return original(file, image)
  }

  const target = computeTargetSize(image.width, image.height, maxEdge)
  const transparent = file.type === 'image/png' || file.type === 'image/webp' ? deps.hasTransparency(image) : false
  const mime = transparent ? 'image/png' : 'image/jpeg'
  const encoded = await deps.encode(image, target.width, target.height, mime, JPEG_QUALITY)

  if (encoded === null) {
    if (heic) throw new UnsupportedImageError(true)
    return original(file, image)
  }
  if (!heic && encoded.size >= file.size) {
    return original(file, image)
  }
  return {
    blob: encoded,
    mime,
    width: target.width,
    height: target.height,
    bytes: encoded.size,
    name: replaceExtension(file.name, transparent ? 'png' : 'jpg'),
  }
}
